import { useCallback, useMemo, useState } from "react";
import { type IssueRow, IssuesDocument } from "#app/lib/data";
import { applyCurrentUserFilter } from "#app/lib/query-utils";
import {
  useGroupQuery,
  type GroupQueryState,
} from "#app/lib/hooks/use-group-query";
import type { Omittable } from "@salestrak/ui";
import { DEFAULT_PAGE_SIZE } from "#app/lib/constants/page-size";

// Windowed paginated issues for a single kanban column. Dimension-neutral: the
// consumer passes the column's COMPLETE `where` (grouping selector + base
// filter); this runs the shared `Issues` query with offset pagination via
// `useGroupQuery`. Cache merge is offsetPagination(["where"]) in
// apollo-client.ts; the column assembles the window with `useWindowedPages`.
//
// `onlyCurrentUser` is folded INTO `where` so it drives both the variablesKey
// reset and the per-`where` cache key — toggling "Only Me" re-subscribes each
// column exactly once, and toggling back is a cache hit.
//
// `where` MUST be a stable reference per column (memoize it) — a new object
// identity each render re-subscribes the column.

/** The board column page size — the single source consumed by both this query hook
 *  and the BoardView column's `pageSize` (they must match). */
export const COLUMN_PAGE_SIZE = DEFAULT_PAGE_SIZE;
const EMPTY_WHERE: Record<string, unknown> = {};

// Manual board order — the kanban sortOrder set by drag. A collection board
// overrides this with a Customize view sort; within-column order then
// follows that sort instead of the manual order.
const DEFAULT_ORDER_BY: Record<string, unknown> = { sortOrder: 1 };

const NOOP_FETCH_PAGE = () => {};

const INITIAL_STATE: GroupQueryState<IssueRow> = {
  data: [],
  total: 0,
  loading: true,
  dataOffset: 0,
  fetchPage: NOOP_FETCH_PAGE,
  variablesKey: "",
};

type UseColumnIssuesOptions = Omittable<{
  /** The column's complete filter (grouping selector + any base filter). The
   *  "Only Me" flag is folded on top by this hook. */
  where?: Record<string, unknown>;
  onlyCurrentUser?: boolean;
  enabled?: boolean;
  /** Column ordering. Defaults to manual board order (`{ sortOrder: 1 }`). Pass
   *  a stable reference — a new object identity each render re-subscribes. */
  orderBy?: Record<string, unknown>;
}>;

export function useColumnIssues({
  where = EMPTY_WHERE,
  onlyCurrentUser,
  enabled = true,
  orderBy = DEFAULT_ORDER_BY,
}: UseColumnIssuesOptions) {
  // Fold "Only Me" into the where so it participates in the cache key and the
  // variablesKey reset.
  const effectiveWhere = useMemo(
    () => applyCurrentUserFilter(where, !!onlyCurrentUser),
    [where, onlyCurrentUser],
  );

  const variables = useMemo(
    () => ({ where: effectiveWhere, orderBy, limit: COLUMN_PAGE_SIZE }),
    [effectiveWhere, orderBy],
  );

  const [state, setState] =
    useState<GroupQueryState<IssueRow>>(INITIAL_STATE);

  const onStateChange = useCallback(
    (_key: string, next: GroupQueryState<IssueRow>) =>
      setState(next),
    [],
  );

  useGroupQuery<IssueRow>({
    groupKey: "column",
    document: IssuesDocument,
    variables,
    getConnection: (d) => d?.issues,
    onStateChange,
    enabled,
  });

  return {
    issues: state.data,
    dataOffset: state.dataOffset,
    total: state.total,
    loading: state.loading,
    fetchPage: state.fetchPage,
    variablesKey: state.variablesKey,
  };
}
